import { useState } from 'react';
import api from '../services/api';

/**
 * @module useCreateQuestionnaire
 * @description Custom hook for creating new questionnaires in the Mental Health Portal application.
 * Handles API communication, loading states, and error handling.
 *
 * @requires react
 * @requires ../services/api
 */
export function useCreateQuestionnaire() {
  /**
   * @state {boolean} loading - Indicates if the API request is in progress
   */
  const [loading, setLoading] = useState(false);

  /**
   * @state {string|null} error - Error message if the API request fails, null otherwise
   */
  const [error, setError] = useState(null);

  /**
   * @state {boolean} success - Indicates if the API request was successful
   */
  const [success, setSuccess] = useState(false);

  /**
   * Creates a new questionnaire by sending data to the API
   * @async
   * @function createQuestionnaire
   * @param {Object} data - The questionnaire data to be created
   * @param {string} data.name - Questionnaire name
   * @param {string} data.toBeAppliedAt - When the questionnaire should be applied (initial, daily, weekly)
   * @param {string} data.category - Questionnaire category
   * @param {string} data.correctionType - Algorithm used for scoring
   * @param {string} data.introduction - Introductory text
   * @param {string} data.createdBy - User ID of creator
   * @param {Array<Object>} data.questions - List of questions
   * @returns {Promise<Object|undefined>} The created questionnaire data if successful, undefined otherwise
   * @throws {Error} If the API request fails
   */
  const createQuestionnaire = async (data) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await api.post('create-questionnaire', data);

      if (response.status === 201) {
        setSuccess(true);
        return response.data;
      } else {
        throw new Error('Erro ao criar questionário.');
      }
    } catch (err) {
      console.error('Erro ao criar questionário:', err);
      setError(err.message || 'Erro inesperado.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * @typedef {Object} UseCreateQuestionnaireReturn
   * @property {Function} createQuestionnaire - Function to create a new questionnaire
   * @property {boolean} loading - Indicates if the API request is in progress
   * @property {string|null} error - Error message if the API request fails, null otherwise
   * @property {boolean} success - Indicates if the API request was successful
   */

  /**
   * @returns {UseCreateQuestionnaireReturn} The hook's return values
   */
  return {
    createQuestionnaire,
    loading,
    error,
    success,
  };
}
